import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_HOTELAPI_BASE_URL;

if (!process.env.REACT_APP_HOTELAPI_BASE_URL) {
  console.info(
    "Using fallback Hotel API URL: http://46.62.206.214:1661 (Env var not detected)",
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCENARIO SWITCH
//   true  → use hardcoded mock data (current, no endpoint yet)
//   false → call the real API endpoint (set this to false when endpoint is live)
// ─────────────────────────────────────────────────────────────────────────────
const USE_MOCK = false;

// ─────────────────────────────────────────────────────────────────────────────
// MOCK DATA
// Same shape the API is expected to return once it's live.
// Each item: { name, hotelIds[], type:"city"|"hotel", countryCode, countryName, state }
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_SUGGESTIONS = [
  {
    name: "New Delhi and NCR",
    hotelIds: ["100000000003"],
    type: "city",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi National Territory",
  },
  {
    name: "New Delhi",
    hotelIds: [
      "100000000036",
      "100000000038",
      "100000000051",
      "100000000053",
      "100000000058",
      "100000000064",
      "100000000067",
      "100000000071",
      "100000000075",
      "100000000087",
      "100000000088",
      "100000000090",
      "100000000092",
    ],
    type: "city",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi",
  },
  {
    name: "The Oberoi, New Delhi",
    hotelIds: ["100000000036"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi",
  },
  {
    name: "Taj Mahal, New Delhi",
    hotelIds: ["100000000038"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi N.C.R",
  },
  {
    name: "Taj Palace, New Delhi",
    hotelIds: ["100000000051"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi",
  },
  {
    name: "The Grand New Delhi",
    hotelIds: ["100000000053"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi N.C.R",
  },
  {
    name: "Park Inn by Radisson New Delhi Lajpat Nagar",
    hotelIds: ["100000000058"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi N.C.R",
  },
  {
    name: "The Suryaa New Delhi",
    hotelIds: ["100000000067"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi N.C.R",
  },
  {
    name: "Hotel Kohinoor Continental, Airport - Newly Renovated",
    hotelIds: ["100000000083"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Maharashtra",
  },
  {
    name: "Ambassador, New Delhi - IHCL SeleQtions",
    hotelIds: ["100000000088"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi N.C.R",
  },
  {
    name: "The Hans Hotel New Delhi",
    hotelIds: ["100000000092"],
    type: "hotel",
    countryCode: "IN",
    countryName: "India",
    state: "Delhi N.C.R",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SCENARIO 1 — Mock (filter locally)
// ─────────────────────────────────────────────────────────────────────────────
const fetchFromMock = (searchText) => {
  const lower = searchText.toLowerCase();
  return MOCK_SUGGESTIONS.filter((item) =>
    item.name.toLowerCase().includes(lower),
  ).map((item) => ({
    ...item,
    region: item.region || [item.name, item.state, item.countryName].filter(Boolean).join(", "),
  }));
};

// ─────────────────────────────────────────────────────────────────────────────
// SCENARIO 2 — Real API
// Expected endpoint: GET /api/StaticContent/autosuggest-new
// Request query parameter: input (e.g. ?input=Delhi)
// Expected response shape: { suggestions: [ { name, hotelIds, type, countryCode, countryName, state, region } ] }
// ─────────────────────────────────────────────────────────────────────────────
const fetchFromApi = async (searchText) => {
  try {
    const finalUrl = `${API_BASE_URL}/api/StaticContent/autosuggest-new`;

    const response = await axios.get(
      `${finalUrl}?input=${encodeURIComponent(searchText)}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );

    const data = response.data?.suggestions || response.data || [];
    return data.map((item) => ({
      name: item.name || item.cityName || item.hotelName,
      hotelIds: item.hotelIds || (item.hotelId ? [item.hotelId] : []),
      type: item.type || (item.hotelId ? "hotel" : "city"),
      countryCode: item.countryCode || "IN",
      countryName: item.countryName || "India",
      state: item.state || item.stateName || "",
      region: item.region || "",
    }));
  } catch (error) {
    console.warn(
      "Hotel AutoSuggest API failed, falling back to mock data:",
      error.message,
    );
    // Graceful fallback to mock when API is unavailable
    return fetchFromMock(searchText);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT — use this in HotelSearch.js and ModifySearch.js
// When USE_MOCK is true  → Scenario 1 (mock, instant, no network)
// When USE_MOCK is false → Scenario 2 (live API, with mock fallback on error)
// ─────────────────────────────────────────────────────────────────────────────
export const getHotelSuggestions = async (searchText) => {
  if (!searchText || searchText.length < 3) return [];
  if (USE_MOCK) return fetchFromMock(searchText);
  return await fetchFromApi(searchText);
};

export { MOCK_SUGGESTIONS };
